import { useState } from "react";
import { Link } from "react-router-dom";
import { usePayment } from "../../hooks/usePayment";
import { useTelemetry } from "../../hooks/useTelemetry";
import { MODULES } from "../../telemetry/constants/modules";
import { ACTIONS } from "../../telemetry/constants/actions";

// interface Props {
//   userId: string;
//   planId: string;
//   label?: string;
//   disabled?: boolean;
//   couponCode?: string;
// }
interface Props {
  userId: string;
  planId: string;
  planTypeId?: number;
  couponCode?: string;
  label?: string;
  disabled?: boolean;
}

export default function PaymentButton({
  userId,
  planId,
  planTypeId,
  couponCode,
  label = "Buy Now",
  disabled = false,
}: Props){

  // const { trackAction } = useTelemetry({ module: MODULES.BILLING, submodule: 'Subscription Plans' });

  const {
    loading,
    startSubscriptionCheckout,
  } = usePayment();

  const [missingContactFields, setMissingContactFields] = useState<string | null>(null);
  const [showServiceUnavailableModal, setShowServiceUnavailableModal] = useState(false);
 
  const handleCheckout =
    async () => {

      if (disabled) {
        return;
      }

      try {

        const payment =
          await startSubscriptionCheckout(
            userId,
            planId,
            planTypeId,
            couponCode
          );

        if (!payment) {
          return;
        }

        // trackAction(ACTIONS.INITIATE_PAYMENT, 'Initiate Checkout Payment', {
        //   plan_id: planId,
        //   coupon_code: couponCode || undefined,
        // });

        const formFields = payment.checkout_type === "merchant_hosted"
          ? payment.checkout_payload
          : payment.params;

        if (!payment.action_url || !formFields) {
          console.error("Invalid payment response: missing action_url or params");
          return;
        }

        const form =
          document.createElement("form");

        form.method = "POST";

        form.action =
          payment.action_url;

        Object.entries(
          formFields
        ).forEach(([key, value]) => {

          const input =
            document.createElement(
              "input"
            );

          input.type = "hidden";
          input.name = key;
          input.value = String(value);

          form.appendChild(
            input
          );

        });

        document.body.appendChild(
          form
        );

        form.submit();

      } catch (error: any) {

        console.error(
          "Checkout failed:",
          error
        );
        
        const detail = error?.response?.data?.detail;
        if (detail === "MISSING_PHONE_AND_EMAIL" || detail === "MISSING_USER_CONTACT_INFO") {
          setMissingContactFields("both");
        } else if (detail === "MISSING_PHONE") {
          setMissingContactFields("phone");
        } else if (detail === "MISSING_EMAIL") {
          setMissingContactFields("email");
        } else if (detail === "SERVICE_UNAVAILABLE_PAYMENT_CONFIG") {
          setShowServiceUnavailableModal(true);
        }

      }

    };

  return (
    <>
    <button
      onClick={handleCheckout}
      disabled={
        loading || disabled
      }
      className="
        w-full
        py-4
        px-6
        rounded-xl
        bg-[#0859B8]
        text-[#FDFFFC]
        font-heading font-black text-sm uppercase tracking-wider
        transition-colors
        duration-300
        hover:bg-[#064893]
        disabled:opacity-50
        disabled:cursor-not-allowed
        cursor-pointer
      "
    >

      {loading ? (

        <span className="flex items-center justify-center gap-2">

          <span
            className="
              w-4
              h-4
              border-2
              border-white
              border-t-transparent
              rounded-full
              animate-spin
            "
          />

          Processing...

        </span>

      ) : (

        <span className="flex items-center justify-center gap-2">

          <i className="ri-secure-payment-line"></i>

          {label}

        </span>

      )}

    </button>
    
    {/* Update Profile Modal */}
    {missingContactFields && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-800 text-center mb-2">Update Required</h3>
            <p className="text-slate-600 text-center mb-6">
              Please update your {missingContactFields === "both" ? "mobile number and email" : missingContactFields === "phone" ? "mobile number" : "email address"} in your profile to proceed with payment.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setMissingContactFields(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-semibold transition-colors"
              >
                Cancel
              </button>
              <Link
                to="/profile"
                className="px-4 py-2 rounded-xl bg-[#0859B8] text-white hover:bg-[#064893] font-semibold transition-colors"
              >
                Go to Profile
              </Link>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Service Unavailable Modal */}
    {showServiceUnavailableModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-server-line text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-800 text-center mb-2">Service Unavailable</h3>
            <p className="text-slate-600 text-center mb-6">
              Payment service unavailable. Please connect to the support agent.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowServiceUnavailableModal(false)}
                className="px-6 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}